/**
 * KeyRisksIssuesSummary — Live 2-up row for Key Risks and Key Issues
 * on the Summary tab, below the Phases timeline.
 *
 * Fetches risks/issues for the project and filters to those flagged
 * as isKeyRisk / isKeyIssue. Clicking an item opens the shared
 * RiskDrawer / IssueDrawer in read-only mode (or edit mode if the
 * user has edit rights via canEditProjectContent).
 *
 * TODO (DEV-738 cutover): Replace canEdit (V1 canEditProjectContent)
 * with hasCapability('risks:view') for visibility and
 * hasCapability('risks:edit') for edit controls. See ADR-001.
 *
 * Epic: DEV-683
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchProjectRisks, updateProjectRisk, deleteProjectRisk, materializeRisk, type ProjectRiskResponse } from '../../api/projectRisks';
import { fetchProjectIssues, updateProjectIssue, deleteProjectIssue, type ProjectIssueResponse } from '../../api/projectIssues';
import { fetchAllSecurityUsers, type SecurityUserResponse } from '../../api/security';
import { fetchProjectRoleSlots, type ProjectRoleSlotResponse } from '../../api/projectRoles';
import { riskScoreBgColor } from '../RisksIssuesTab/riskScoreColor';
import { RiskDrawer, IssueDrawer } from '../RisksIssuesTab/RiskIssueDrawers';
import { deriveRisksTint, deriveIssuesTint } from './cardTint';
import styles from './PerformanceSummary.module.css';

const SEVERITY_COLORS: Record<string, string> = {
  blocking: 'var(--color-error)',
  warning: 'var(--color-warning)',
  informational: 'var(--text-muted)',
};

function guidKey(g: string | null | undefined): string {
  return (g ?? '').toUpperCase();
}

type Props = {
  projectGuid: string;
  /** V1 permission gate. TODO (DEV-738): replace with hasCapability('risks:edit'). */
  canEdit?: boolean;
};

const RisksIssuesPlaceholder: React.FC<Props> = ({ projectGuid, canEdit = false }) => {
  const { t } = useAppTranslation('kpi');
  const { t: tPages } = useAppTranslation('pages');

  /* ── Data ─────────────────────────────────────────────────────── */
  const [risks, setRisks] = useState<ProjectRiskResponse[]>([]);
  const [issues, setIssues] = useState<ProjectIssueResponse[]>([]);
  const [keyRisks, setKeyRisks] = useState<ProjectRiskResponse[]>([]);
  const [keyIssues, setKeyIssues] = useState<ProjectIssueResponse[]>([]);
  const [userMap, setUserMap] = useState<Map<string, SecurityUserResponse>>(new Map());
  const [roleSlots, setRoleSlots] = useState<ProjectRoleSlotResponse[]>([]);

  /* ── Drawer state ────────────────────────────────────────────── */
  const [selectedRisk, setSelectedRisk] = useState<ProjectRiskResponse | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<ProjectIssueResponse | null>(null);
  const [drawerEditing, setDrawerEditing] = useState(false);
  const [riskDraft, setRiskDraft] = useState<Partial<ProjectRiskResponse>>({});
  const [issueDraft, setIssueDraft] = useState<Partial<ProjectIssueResponse>>({});
  const [drawerBusy, setDrawerBusy] = useState(false);

  /* ── Fetch ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!projectGuid) return;
    let cancelled = false;

    (async () => {
      try {
        const [riskData, issueData, users, slots] = await Promise.all([
          fetchProjectRisks(projectGuid),
          fetchProjectIssues(projectGuid),
          fetchAllSecurityUsers().catch(() => [] as SecurityUserResponse[]),
          fetchProjectRoleSlots(projectGuid).catch(() => [] as ProjectRoleSlotResponse[]),
        ]);
        if (cancelled) return;

        setRisks(riskData);
        setIssues(issueData);
        setKeyRisks(riskData.filter((r) => r.isKeyRisk));
        setKeyIssues(issueData.filter((i) => i.isKeyIssue));
        setRoleSlots(slots.filter((s) => s.status !== 'closed'));

        const um = new Map<string, SecurityUserResponse>();
        users.forEach((u) => um.set(guidKey(u.userGuid), u));
        setUserMap(um);
      } catch (err: any) {
        console.warn('[KeyRisksIssues] API fetch failed:', err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [projectGuid]);

  /* ── Name resolution ─────────────────────────────────────────── */
  const resolveUser = useCallback(
    (guid: string | null | undefined): string => {
      if (!guid) return tPages('projectDetail.risksIssues.unknownUser');
      const u = userMap.get(guidKey(guid));
      return u?.displayName || u?.name || tPages('projectDetail.risksIssues.unknownUser');
    },
    [userMap, tPages],
  );

  const resolveRoleSlot = useCallback(
    (slotGuid: string | null | undefined): string => {
      if (!slotGuid) return tPages('projectDetail.risksIssues.unassigned');
      const slot = roleSlots.find(
        (s) => s.roleSlotGuid.toUpperCase() === slotGuid.toUpperCase(),
      );
      return slot?.roleLabel || tPages('projectDetail.risksIssues.unassigned');
    },
    [roleSlots, tPages],
  );

  /* ── Risk mutations (edit-mode only) ─────────────────────────── */
  const saveRisk = useCallback(async () => {
    if (!selectedRisk || Object.keys(riskDraft).length === 0) return;
    setDrawerBusy(true);
    try {
      const updated = await updateProjectRisk(projectGuid, selectedRisk.id, riskDraft as any);
      setRisks((prev) => prev.map((r) => (r.id === selectedRisk.id ? updated : r)));
      setKeyRisks((prev) => {
        const without = prev.filter((r) => r.id !== selectedRisk.id);
        return updated.isKeyRisk ? [...without, updated].sort((a, b) => a.title.localeCompare(b.title)) : without;
      });
      setSelectedRisk(updated);
      setDrawerEditing(false);
      setRiskDraft({});
    } catch (e: any) {
      console.error('[KeyRisksIssues] save risk failed:', e.message);
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, riskDraft]);

  const handleDeleteRisk = useCallback(async () => {
    if (!selectedRisk) return;
    if (typeof window !== 'undefined' && !window.confirm(tPages('projectDetail.risksIssues.confirmDelete'))) return;
    setDrawerBusy(true);
    try {
      await deleteProjectRisk(projectGuid, selectedRisk.id);
      setRisks((prev) => prev.filter((r) => r.id !== selectedRisk.id));
      setKeyRisks((prev) => prev.filter((r) => r.id !== selectedRisk.id));
      setSelectedRisk(null);
    } catch (e: any) {
      console.error('[KeyRisksIssues] delete risk failed:', e.message);
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, tPages]);

  const handleMaterialize = useCallback(async () => {
    if (!selectedRisk) return;
    if (typeof window !== 'undefined' && !window.confirm(tPages('projectDetail.risksIssues.confirmMaterialize'))) return;
    setDrawerBusy(true);
    try {
      const result = await materializeRisk(projectGuid, selectedRisk.id);
      setRisks((prev) => prev.map((r) => (r.id === selectedRisk.id ? result.risk : r)));
      setSelectedRisk(result.risk);
      const freshIssues = await fetchProjectIssues(projectGuid);
      setIssues(freshIssues);
      setKeyIssues(freshIssues.filter((i) => i.isKeyIssue));
    } catch (e: any) {
      console.error('[KeyRisksIssues] materialize failed:', e.message);
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, tPages]);

  /* ── Issue mutations (edit-mode only) ────────────────────────── */
  const saveIssue = useCallback(async () => {
    if (!selectedIssue || Object.keys(issueDraft).length === 0) return;
    setDrawerBusy(true);
    try {
      const updated = await updateProjectIssue(projectGuid, selectedIssue.id, issueDraft as any);
      setIssues((prev) => prev.map((i) => (i.id === selectedIssue.id ? updated : i)));
      setKeyIssues((prev) => {
        const without = prev.filter((i) => i.id !== selectedIssue.id);
        return updated.isKeyIssue ? [...without, updated].sort((a, b) => a.title.localeCompare(b.title)) : without;
      });
      setSelectedIssue(updated);
      setDrawerEditing(false);
      setIssueDraft({});
    } catch (e: any) {
      console.error('[KeyRisksIssues] save issue failed:', e.message);
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedIssue, issueDraft]);

  const handleDeleteIssue = useCallback(async () => {
    if (!selectedIssue) return;
    if (typeof window !== 'undefined' && !window.confirm(tPages('projectDetail.risksIssues.confirmDelete'))) return;
    setDrawerBusy(true);
    try {
      await deleteProjectIssue(projectGuid, selectedIssue.id);
      setIssues((prev) => prev.filter((i) => i.id !== selectedIssue.id));
      setKeyIssues((prev) => prev.filter((i) => i.id !== selectedIssue.id));
      setSelectedIssue(null);
    } catch (e: any) {
      console.error('[KeyRisksIssues] delete issue failed:', e.message);
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedIssue, tPages]);

  /* ── Header tint derivation (DEV-853) ────────────────────────── */
  /* Operates only on the displayed (key) items so the band matches
   * what the user sees in the card body. */
  const risksTint = deriveRisksTint(keyRisks);
  const issuesTint = deriveIssuesTint(keyIssues);

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <>
      <div className={styles.twoUp}>
        {/* Key Risks */}
        <section
          className={`${styles.card} ${styles.tintableCard}`}
          aria-labelledby="risks-title"
        >
          <div
            className={styles.tintableCardHeader}
            data-tint={risksTint ?? undefined}
          >
            <h3 id="risks-title" className={styles.cardTitle}>
              {t('performanceSummary.risks.title')}
            </h3>
          </div>
          <div className={styles.tintableCardBody}>
            {keyRisks.length === 0 ? (
              <div className={styles.empty}>
                {t('performanceSummary.risks.empty')}
              </div>
            ) : (
              <div className={styles.keyItemRows}>
                {keyRisks.map((r) => {
                  const score = (r.likelihood ?? 1) * (r.impact ?? 1);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={styles.keyItemRow}
                      onClick={() => {
                        setSelectedRisk(r);
                        setDrawerEditing(false);
                        setRiskDraft({});
                      }}
                      title={t('performanceSummary.risks.clickToOpen')}
                    >
                      <span
                        className={styles.keyItemBadge}
                        style={{ background: riskScoreBgColor(score) }}
                        title={`${t('performanceSummary.risks.score')}: ${score}`}
                      >
                        {score}
                      </span>
                      <span className={styles.keyItemTitle} title={r.title}>
                        {r.title}
                      </span>
                      <span className={styles.keyItemStatus}>
                        {r.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Key Issues */}
        <section
          className={`${styles.card} ${styles.tintableCard}`}
          aria-labelledby="issues-title"
        >
          <div
            className={styles.tintableCardHeader}
            data-tint={issuesTint ?? undefined}
          >
            <h3 id="issues-title" className={styles.cardTitle}>
              {t('performanceSummary.issues.title')}
            </h3>
          </div>
          <div className={styles.tintableCardBody}>
            {keyIssues.length === 0 ? (
              <div className={styles.empty}>
                {t('performanceSummary.issues.empty')}
              </div>
            ) : (
              <div className={styles.keyItemRows}>
                {keyIssues.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    className={styles.keyItemRow}
                    onClick={() => {
                      setSelectedIssue(i);
                      setDrawerEditing(false);
                      setIssueDraft({});
                    }}
                    title={t('performanceSummary.issues.clickToOpen')}
                  >
                    <span
                      className={styles.keyItemBadge}
                      style={{ background: SEVERITY_COLORS[i.severity] ?? 'var(--text-muted)' }}
                    >
                      {t(`performanceSummary.issues.severity_${i.severity}`)}
                    </span>
                    <span className={styles.keyItemTitle} title={i.title}>
                      {i.title}
                    </span>
                    <span className={styles.keyItemStatus}>
                      {i.status?.replace('_', ' ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Risk Drawer — portalled to body */}
      {selectedRisk && createPortal(
        <RiskDrawer
          risk={selectedRisk}
          editing={drawerEditing}
          setEditing={setDrawerEditing}
          draft={riskDraft}
          setDraft={setRiskDraft}
          onSave={saveRisk}
          onClose={() => {
            setSelectedRisk(null);
            setDrawerEditing(false);
            setRiskDraft({});
          }}
          onDelete={handleDeleteRisk}
          onMaterialize={handleMaterialize}
          canEdit={canEdit}
          busy={drawerBusy}
          resolveUser={resolveUser}
          resolveRoleSlot={resolveRoleSlot}
          roleSlots={roleSlots}
          t={tPages}
        />,
        document.body,
      )}

      {/* Issue Drawer — portalled to body */}
      {selectedIssue && createPortal(
        <IssueDrawer
          issue={selectedIssue}
          editing={drawerEditing}
          setEditing={setDrawerEditing}
          draft={issueDraft}
          setDraft={setIssueDraft}
          onSave={saveIssue}
          onClose={() => {
            setSelectedIssue(null);
            setDrawerEditing(false);
            setIssueDraft({});
          }}
          onDelete={handleDeleteIssue}
          canEdit={canEdit}
          busy={drawerBusy}
          resolveUser={resolveUser}
          resolveRoleSlot={resolveRoleSlot}
          roleSlots={roleSlots}
          t={tPages}
        />,
        document.body,
      )}
    </>
  );
};

export default RisksIssuesPlaceholder;
